import React from 'react';
import { Card, CardContent } from './ui/card';
import { Heart, Quote } from 'lucide-react';

export const FoundersMessage = () => {
  return (
    <section id="founders-message" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-coralRed/10 px-6 py-2 rounded-full mb-4">
            <span className="text-coralRed font-semibold">Our Story</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">A Message from Our Founder</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Shannon Emmer shares her journey and vision for transforming education
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Founder Image & Info */}
          <div className="lg:col-span-1">
            <Card className="border-none shadow-xl sticky top-24">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-tealAccent/20 to-sageGreen/20 rounded-2xl p-8 mb-6 text-center">
                  <div className="mb-4">
                    <img 
                      src={process.env.REACT_APP_FOUNDER_PHOTO_URL} 
                      alt="Shannon Emmer, Founder" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-white shadow-lg"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-navyBlue mb-2">Shannon Emmer</h3>
                  <p className="text-sageGreen font-semibold mb-4">Founder & Visionary</p>
                  <div className="h-1 w-16 bg-tealAccent mx-auto"></div>
                </div>
                
                <div className="space-y-3 text-sm text-gray-600">
                  <p className="flex items-start space-x-2">
                    <span className="text-tealAccent mt-1">•</span>
                    <span>Middle School Educator</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-tealAccent mt-1">•</span>
                    <span>Education Innovator</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-tealAccent mt-1">•</span>
                    <span>Advocate for Teacher Leadership</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Message Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Opening Quote */}
            <div className="relative bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 sm:p-10 text-white">
              <Quote className="absolute top-6 left-6 text-tealAccent/30" size={48} />
              <p className="text-2xl font-serif italic leading-relaxed relative z-10 pl-8">
                "What if teachers were trusted to lead again?"
              </p>
            </div>

            {/* Story Paragraphs */}
            <div className="bg-warmCream rounded-2xl p-8 space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                My journey into education began with my mother, a brilliant middle school teacher. From her, I learned that <span className="font-semibold text-navyBlue">education is not just about lessons and tests, but about creating safety, belonging, and possibility</span>. This early exposure instilled in me a purpose to guide, nurture, and ignite curiosity in students.
              </p>

              <p className="text-lg">
                I found my calling in the middle school classroom, where teaching has been more than a profession—it has been the center of my identity. My classroom wasn't just a place for instruction; it was a place where children felt <span className="font-semibold text-sageGreen">seen, heard, and valued</span>. I witnessed firsthand the transformative power of love and belief, and the impact of teachers who persevere with students when others have given up.
              </p>

              <p className="text-lg">
                But I also witnessed something else: how often the very people who give everything to their students are <span className="font-semibold text-coralRed">silenced, overlooked, and burned out</span>. I saw their voices dismissed, their creativity restricted, and their wellbeing neglected. I watched incredible teachers—mentors, innovators, dreamers—pushed out of the profession not because they stopped caring, but because the system stopped caring about them.
              </p>
            </div>

            {/* The Vision */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-tealAccent space-y-6">
              <h3 className="text-2xl font-bold text-navyBlue mb-4">Born from Heartbreak and Hope</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-tealAccent">Doctrina Charter School was born out of that heartbreak—and out of hope.</span> It is the answer to a question that haunted me: What if teachers were trusted to lead again?
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Doctrina exists to restore power, respect, and humanity to education. Every element of this school—from its educator-led governance to its project-based curriculum and restorative culture—is rooted in the belief that <span className="font-semibold text-goldenOlive">schools should be designed with teachers, not around them</span>.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our mission is to reimagine what public education can be when passion, professionalism, and compassion guide every decision.
              </p>
            </div>

            {/* Why I Founded Doctrina */}
            <div className="bg-gradient-to-br from-tealAccent/10 to-sageGreen/10 rounded-2xl p-8 space-y-4">
              <h3 className="text-2xl font-bold text-navyBlue mb-4">Why I Founded Doctrina</h3>
              <div className="space-y-4 text-gray-700">
                <p className="flex items-start space-x-3">
                  <Heart className="text-coralRed flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg">I believe in the teachers who still stay late, spend their own money, and refuse to give up on their students.</span>
                </p>
                <p className="flex items-start space-x-3">
                  <Heart className="text-tealAccent flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg">I believe in the students who are waiting for someone to see their brilliance and tell them they are capable of greatness.</span>
                </p>
                <p className="flex items-start space-x-3">
                  <Heart className="text-sageGreen flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg">I believe in healing what is broken—in education, and in all of us who were called to this work.</span>
                </p>
              </div>
            </div>

            {/* Closing Statement */}
            <div className="bg-navyBlue rounded-2xl p-8 sm:p-10 text-white text-center">
              <p className="text-2xl font-bold mb-4">Turning Pain into Purpose</p>
              <p className="text-lg leading-relaxed">
                This school is my way of turning pain into purpose. It is the place I always wished existed—for teachers, for students, and for the future of learning itself.
              </p>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-tealAccent font-semibold text-lg">— Shannon Emmer</p>
                <p className="text-white/80 text-sm mt-1">Founder, Doctrina Charter School</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundersMessage;